import React from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Alert,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Grid,
	Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	blue_pilu,
	formatEventData,
	isEmptyOrNull,
	white_pilu,
} from '../../../helpers/utils';
import { EVENT_FIELDS_EN_ES_OBJ } from '../../utils/lists';

// Styles
const trStyle = {
	backgroundColor: blue_pilu,
	color: white_pilu,
};

const cellStyle = {
	width: '100px',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

// Helper function to get the Spanish label for a field
const getFieldLabelInSpanish = (fieldName) => EVENT_FIELDS_EN_ES_OBJ[fieldName];

// Components for various sections

// Table Headings
const TableHeadings = () => (
	<TableHead>
		<TableRow>
			<TableCell style={trStyle}>#</TableCell>
			<TableCell style={trStyle}>Lugar</TableCell>
			<TableCell style={trStyle}>Usuario</TableCell>
			<TableCell style={trStyle}>Correo del usuario</TableCell>
			<TableCell style={trStyle}>División</TableCell>
		</TableRow>
	</TableHead>
);

// Table Row for each item
const TableItemRow = ({ item, index }) => (
	<React.Fragment key={index}>
		<TableRow>
			<TableCell style={cellStyle}>{index + 1}</TableCell>
			<TableCell style={cellStyle}>{item.place_name}</TableCell>
			<TableCell style={cellStyle}>
				{item?.user?.display_name ?? '-'}
			</TableCell>
			<TableCell style={cellStyle}>{item?.user?.email ?? '-'}</TableCell>
			<TableCell style={cellStyle}>
				{item.division?.length > 0 ? item.division : 'No asignado'}
			</TableCell>
		</TableRow>
		<TableRow>
			<TableCell colSpan={6} style={{ padding: 0 }}>
				<DetailsAccordion item={formatEventData(item)} index={index} />
			</TableCell>
		</TableRow>
	</React.Fragment>
);

// Accordion to show more details
const DetailsAccordion = ({ item, index }) => (
	<Accordion>
		<AccordionSummary
			expandIcon={<ExpandMoreIcon />}
			aria-controls={`panel${index}-content`}
			id={`panel${index}-header`}
		>
			<Typography>Ver más detalles</Typography>
		</AccordionSummary>
		<AccordionDetails>
			<Grid container spacing={2}>
				{/* Core Information */}
				<SectionTitle title='Información Principal' />
				{renderFields(item, [
					'name',
					'datetime',
					'address',
					'place_name',
				])}

				<Divider />

				{/* User Information */}
				<SectionTitle title='Información del Usuario' />
				{renderFields(item, [
					'user_id',
					'user_email',
					'user_ref',
					'user_name',
				])}

				<Divider />

				{/* Operational and Status Information */}
				<SectionTitle title='Estado Operacional' />
				{renderFields(item, [
					'first_check_time',
					'second_check_time',
					'visit_time',
					'created_at',
					'division',
					'car',
					'has_been_postpone',
					'justification_postpone',
					'cancelled',
					'cancelled_time',
					'justification_cancel',
				])}

				<Divider />

				{/* Supplementary Information */}
				<SectionTitle title='Reporte de visita' />
				{renderFields(item, [
					'rate',
					'rate_reason',
					'no_sales_reason',
					'no_sale_subreason',
					'visit_reason',
					'visit_subreason',
					'client_sector',
					'client_level',
					'client_type',
					'extra_notes',
				])}
			</Grid>
		</AccordionDetails>
	</Accordion>
);

// Helper function to render specific fields based on the priority
const renderFields = (item, fieldKeys) => {
	const notEmptyFields = fieldKeys.filter((key) => !isEmptyOrNull(item[key]));
	return notEmptyFields.map((key) => (
		<DetailItem key={key} fieldKey={key} value={item[key]} />
	));
};

// Section Title component for accordion sections
const SectionTitle = ({ title }) => (
	<Grid item xs={12}>
		<Typography variant='h6'>
			<b>
				<u>{title}</u>
			</b>
		</Typography>
	</Grid>
);

// Individual Detail Item inside Accordion
const DetailItem = ({ fieldKey, value }) => (
	<Grid item xs={6}>
		<Typography variant='body1' color='textSecondary'>
			<b>{getFieldLabelInSpanish(fieldKey)}:</b>
		</Typography>
		<Typography variant='body2' color='textPrimary'>
			{typeof value === 'object'
				? JSON.stringify(value)
				: value?.toString() ?? '-'}
		</Typography>
	</Grid>
);

// Main IndicatorDialog component
export const IndicatorDialog = ({ name, open, handleClose, data = [] }) => (
	<Dialog
		open={open}
		onClose={handleClose}
		maxWidth={data?.length > 0 ? 'xl' : 'sm'}
		fullWidth
	>
		<DialogTitle>{name}</DialogTitle>
		<DialogContent>
			{data?.length > 0 ? (
				<TableContainer
					component={Paper}
					style={{ maxHeight: '500px' }}
				>
					<Table stickyHeader>
						<TableHeadings />
						<TableBody>
							{data?.map((item, index) => (
								<TableItemRow
									key={index}
									item={item}
									index={index}
								/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			) : (
				<Alert severity='info'>
					<b>No hay citas</b>
				</Alert>
			)}
		</DialogContent>
		<DialogActions>
			<Button onClick={handleClose} color='primary'>
				Ok
			</Button>
		</DialogActions>
	</Dialog>
);
